import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface MonthlyLogFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

// ─── スライダーコンポーネント ─────────────────────────────────────────────────
function RateSlider({
  label,
  value,
  onChange,
  color,
  icon,
  description,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  color: string;
  icon: string;
  description: string;
}) {
  const getEmoji = (v: number) => {
    if (v >= 90) return '🔥';
    if (v >= 70) return '😊';
    if (v >= 50) return '🙂';
    if (v >= 30) return '😐';
    return '😓';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <i className={`${icon} text-base`} style={{ color }}></i>
          {label}
        </label>
        <div className="flex items-center gap-2">
          <span className="text-lg">{getEmoji(value)}</span>
          <span className="text-xl font-bold" style={{ color }}>{value}%</span>
        </div>
      </div>
      <p className="text-xs text-slate-500">{description}</p>
      <div className="relative pt-1">
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${color} 0%, ${color} ${value}%, #e2e8f0 ${value}%, #e2e8f0 100%)`,
          }}
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
}

export default function MonthlyLogForm({ onClose, onSuccess, editData }: MonthlyLogFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [period, setPeriod] = useState('');
  const [objective, setObjective] = useState('');
  const [actions, setActions] = useState('');
  const [adjustments, setAdjustments] = useState('');
  const [nextTodoText, setNextTodoText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);
  const [achievementRate, setAchievementRate] = useState(50);
  const [progressRate, setProgressRate] = useState(50);

  const fetchAvailableThemes = async () => {
    try {
      const { data } = await supabase
        .from('research_overview')
        .select('title')
        .order('created_at', { ascending: false });
      const themes = (data || []).map((item: any) => item.title).filter(Boolean);
      // 重複を除去
      setAvailableThemes([...new Set(themes)] as string[]);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  useEffect(() => {
    fetchAvailableThemes();
    if (editData) {
      setPeriod(editData.period || '');
      setObjective(editData.objective || '');
      setActions(editData.actions || '');
      setAdjustments(editData.adjustments || '');
      const nt = editData.next_todo;
      setNextTodoText(Array.isArray(nt) ? nt.join('\n') : (nt || ''));
      const tg = editData.tags;
      setTagsText(Array.isArray(tg) ? tg.join(', ') : (tg || ''));
      setResearchTheme(editData.research_theme || '');
      setAchievementRate(editData.achievement_rate ?? 50);
      setProgressRate(editData.progress_rate ?? 50);
    }
  }, [editData]);

  const resetForm = (keepTheme: string) => {
    setPeriod('');
    setObjective('');
    setActions('');
    setAdjustments('');
    setNextTodoText('');
    setTagsText('');
    setResearchTheme(keepTheme);
    setAchievementRate(50);
    setProgressRate(50);
  };

  const handleDelete = async () => {
    if (!editData?.id) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('monthly_logs').delete().eq('id', editData.id);
      if (error) throw error;
      setNotification({ type: 'success', message: '進捗ログを削除しました' });
      setTimeout(() => onClose(), 1200);
    } catch (error: any) {
      setNotification({ type: 'error', message: `削除に失敗しました: ${error?.message}` });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) { setNotification({ type: 'error', message: 'ログインが必要です' }); return; }
    if (!period.trim()) { setNotification({ type: 'error', message: '期間は必須です' }); return; }
    if (!objective.trim()) { setNotification({ type: 'error', message: '今月の目的は必須です' }); return; }
    if (!actions.trim()) { setNotification({ type: 'error', message: '実施内容は必須です' }); return; }

    setLoading(true);
    try {
      const nextTodoArr = nextTodoText.split('\n').map(s => s.trim()).filter(Boolean);
      const tagsArr = tagsText.split(',').map(s => s.trim()).filter(Boolean);

      const dataToSave = {
        user_id: user.id,
        period: period.trim(),
        objective: objective.trim(),
        actions: actions.trim(),
        adjustments: adjustments.trim() || null,
        next_todo: nextTodoArr,
        tags: tagsArr,
        research_theme: researchTheme || null,
        outputs: editData?.outputs ?? [],
        achievement_rate: achievementRate,
        progress_rate: progressRate,
        updated_at: new Date().toISOString(),
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('monthly_logs')
          .update(dataToSave)
          .eq('id', editData.id)
          .eq('user_id', user.id);
        if (error) throw error;
        setNotification({ type: 'success', message: '進捗ログを更新しました ✓' });
        setTimeout(() => { if (onSuccess) onSuccess(); else onClose(); }, 1200);
      } else {
        const { error } = await supabase.from('monthly_logs').insert(dataToSave);
        if (error) throw error;
        setNotification({ type: 'success', message: '進捗ログを保存しました ✓ 続けて投稿できます' });
        const savedTheme = researchTheme;
        setTimeout(() => {
          resetForm(savedTheme);
          setNotification(null);
          if (onSuccess) onSuccess();
        }, 1500);
      }
    } catch (err: any) {
      console.error('保存エラー:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
      {notification && (
        <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-bar-chart-line text-teal-600"></i>
          {editData ? '進捗ログを編集' : '進捗ログを追加'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 期間と関連テーマ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              期間 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
              placeholder="例：2024年1月"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              関連テーマ
            </label>
            {availableThemes.length === 0 ? (
              <div className="w-full px-4 py-3 border border-slate-200 bg-slate-50 rounded-lg text-sm text-slate-600">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-slate-400 mt-0.5"></i>
                  <p className="text-xs">テーマが未登録です。先に「テーマと目的」を登録するとここで選択できます。</p>
                </div>
              </div>
            ) : (
              <>
                <select
                  value={researchTheme}
                  onChange={(e) => setResearchTheme(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
                >
                  <option value="">テーマを選択（任意）</option>
                  {availableThemes.map((theme, index) => (
                    <option key={index} value={theme}>{theme}</option>
                  ))}
                </select>
                <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                  <i className="ri-repeat-line"></i>
                  同じテーマで何度でも投稿できます
                </p>
              </>
            )}
          </div>
        </div>

        {/* ─── 達成度・進捗度スライダー ─── */}
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-100 p-5 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-500 rounded-lg">
              <i className="ri-bar-chart-2-line text-white text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-slate-800">達成度・進捗度を記録</h3>
          </div>
          <RateSlider
            label="目標達成度"
            value={achievementRate}
            onChange={setAchievementRate}
            color="#0d9488"
            icon="ri-trophy-line"
            description="今月の目標をどれくらい達成できましたか？"
          />
          <RateSlider
            label="研究進捗度"
            value={progressRate}
            onChange={setProgressRate}
            color="#059669"
            icon="ri-rocket-line"
            description="研究全体の進み具合はどのくらいですか？"
          />
        </div>

        {/* 今月の目的 */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            今月の目的 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
            rows={2}
            placeholder="例：データクリーニングを完了し、記述統計表を作成する"
            required
            maxLength={500}
          />
        </div>

        {/* 実施内容 */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            実施内容 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={actions}
            onChange={(e) => setActions(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
            rows={4}
            placeholder="例：・企業活動基本調査のデータ読み込み&#10;・欠損値の確認と処理&#10;・記述統計表の作成"
            required
            maxLength={500}
          />
        </div>

        {/* 詰まり・修正点 */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            詰まった点・修正点
          </label>
          <textarea
            value={adjustments}
            onChange={(e) => setAdjustments(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
            rows={2}
            placeholder="例：欠損値が特定の業種に集中していることが判明。処理方針を再検討する必要がある。"
            maxLength={500}
          />
        </div>

        {/* 次のToDo */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            次のToDo
            <span className="text-xs font-normal text-slate-500 ml-2">（1行に1つ）</span>
          </label>
          <textarea
            value={nextTodoText}
            onChange={(e) => setNextTodoText(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
            rows={3}
            placeholder="例：DIDの推定を実施する&#10;ロバストネスチェックを追加する"
            maxLength={500}
          />
        </div>

        {/* タグ */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            タグ
            <span className="text-xs font-normal text-slate-500 ml-2">（カンマ区切り）</span>
          </label>
          <input
            type="text"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-900"
            placeholder="例：データ分析, 統計, 回帰分析"
            maxLength={200}
          />
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {editData && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              削除
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                保存中...
              </span>
            ) : editData ? '更新する' : '保存する'}
          </button>
        </div>
      </form>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6 text-sm">この進捗ログを削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer text-sm">キャンセル</button>
              <button onClick={handleDelete} disabled={loading} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm">{loading ? '削除中...' : '削除する'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
